// LiveStreamGoalList.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import LiveStreamGoalItem from '../container/LiveStreamGoalItem.js';
import LiveStreamKaraokeTooltip from '../container/LiveStreamKaraokeTooltip.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import IconAddSource from '../../img/ic_command.svg';
import IconDeviceSource from '../../img/ic_lovense_badge.svg';
import media from '../style/media.js';
import ModalTracking from '../component/ModalTracking.jsx';

import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
  ModalId,
} from '../resource/mixpanel.js';
import { privateShowStatus } from '../resource/liveStreamConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

const MAX_DISPLAY_GOAL = 2; // TODO: remote config
export class LiveStreamGoalList extends React.PureComponent {
  trackUserClick = buttonId => {
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({
          pathname: window.location.pathname,
        }),
        buttonId,
      },
    });
  };
  openLiveStreamKaraokeModal = () => {
    const { openModal } = this.props;
    this.trackUserClick(ButtonId.LiveStream.ButtonCommandList);
    openModal({ id: 'LiveStreamKaraokeModal' });
  };
  openLiveStreamToyDeviceKaraokeModal = () => {
    const { openModal } = this.props;
    this.trackUserClick(ButtonId.LiveStream.ButtonLovenseCommandList);
    openModal({ id: 'LiveStreamToyDeviceKaraokeModal' });
  };
  render() {
    const {
      t,
      hasToyDevice,
      streamId,
      itemIds,
      karaokeIds,
      shouldDisplayKaraokeTooltip,
      status,
    } = this.props;
    const addIconSource = hasToyDevice ? IconDeviceSource : IconAddSource;
    const addButtonTitle = hasToyDevice
      ? t('livestream_lovense', {
          ns: TranslationNamespace.GIFT,
        })
      : t('command_add', {
          ns: TranslationNamespace.GIFT,
        });
    const handleOnClick = hasToyDevice
      ? this.openLiveStreamToyDeviceKaraokeModal
      : this.openLiveStreamKaraokeModal;

    const renderIds = itemIds.slice(0, MAX_DISPLAY_GOAL);
    const hasGoals = !!renderIds?.length;
    const hasKaraokeIds = !!karaokeIds?.length;
    const shouldStopKaraokeIfPerforming =
      [privateShowStatus.PERFORMING, privateShowStatus.PREPARING].includes(
        status
      ) && !hasToyDevice;

    if (!hasKaraokeIds || shouldStopKaraokeIfPerforming) {
      return (
        <StyledLiveStreamGoalList>
          <PausedGoalLabel>
            {t(!hasKaraokeIds ? 'command_is_not_open' : 'command_pause', {
              ns: TranslationNamespace.GIFT,
            })}
          </PausedGoalLabel>
        </StyledLiveStreamGoalList>
      );
    }

    return (
      <StyledLiveStreamGoalList>
        {hasGoals && (
          <ListWrapper>
            <List>
              {renderIds.map(goalId => (
                <LiveStreamGoalItem
                  key={goalId}
                  goalId={goalId}
                  hasToyDevice={hasToyDevice}
                />
              ))}
            </List>
          </ListWrapper>
        )}
        <AddButtonContainer>
          <AddButton
            hasGoals={hasGoals}
            onClick={handleOnClick}
            hasToyDevice={hasToyDevice}
          >
            <AddButtonIcon src={addIconSource} alt="Add" />
            <AddButtonText>{addButtonTitle}</AddButtonText>
          </AddButton>
          {!hasToyDevice && shouldDisplayKaraokeTooltip && hasKaraokeIds && (
            <ModalTracking
              id="LiveStreamKaraokeTooltip"
              modalId={ModalId.LiveStream.CommandHint}
            >
              <LiveStreamKaraokeTooltip streamId={streamId} />
            </ModalTracking>
          )}
        </AddButtonContainer>
      </StyledLiveStreamGoalList>
    );
  }
}

LiveStreamGoalList.propTypes = {
  t: PropTypes.func.isRequired,
  openModal: PropTypes.func,
  karaokeIds: PropTypes.arrayOf(PropTypes.string),
  itemIds: PropTypes.arrayOf(PropTypes.string),
  streamId: PropTypes.string,
  shouldDisplayKaraokeTooltip: PropTypes.bool,
  status: PropTypes.string,
  hasToyDevice: PropTypes.bool,
};

LiveStreamGoalList.defaultProps = {
  openModal: () => null,
  karaokeIds: [],
  itemIds: [],
  streamId: '',
  shouldDisplayKaraokeTooltip: false,
  status: null,
  hasToyDevice: false,
};

const StyledLiveStreamGoalList = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const AddButtonContainer = styled.div`
  position: relative;
`;

const AddButton = styled.button`
  ${ResetButtonStyle};
  width: 128px;
  height: 32px;
  padding: 4px 8px;
  border-top-left-radius: ${({ hasGoals }) => (hasGoals ? '0px' : '4px')};
  border-top-right-radius: ${({ hasGoals }) => (hasGoals ? '0px' : '4px')};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  ${({ hasToyDevice }) =>
    hasToyDevice
      ? css`
          background-color: rgba(246, 65, 141, 0.8);
        `
      : css`
          background-color: rgba(255, 209, 0, 0.8);
        `};

  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  text-align: center;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  ${media.tablet`
    height: 28px;
    font-size: 12px;
  `};
`;

const AddButtonIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 4px;
`;

const AddButtonText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ListWrapper = styled.div`
  max-width: 128px;
  overflow-x: hidden;
`;

const List = styled.div`
  position: relative;
  width: 100%;
  max-height: 68px;
  overflow-y: scroll;
  padding: 4px 0;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  background-color: rgba(25, 25, 25, 0.8);
  transition: height 0.2s linear;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const PausedGoalLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 128px;
  height: 28px;
  padding: 4px 8px;
  opacity: 0.5;
  border-radius: 4px;
  color: #ffffff;
  background-color: rgba(255, 209, 0, 0.8);

  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
`;

export default withTranslation()(LiveStreamGoalList);
